import * as signalR from '@microsoft/signalr';
import { SIGNALR_URL } from './constants';
import { keycloak, getValidToken } from '../auth/keycloak';

// eslint-disable-next-line no-console
console.warn('[signalr] SIGNALR_URL =', SIGNALR_URL);

/**
 * SignalR connection lifecycle.
 *
 * The previous implementation read the bearer from `localStorage.token`,
 * which (a) is never written by the current auth flow — Keycloak stores
 * tokens in memory on the singleton, not localStorage — and (b) used a
 * hard-coded MetroDrive hub URL that pointed at a different product.
 * Both problems caused the connection to fail silently, and the
 * `else` branch of `startSignalRConnection` then logged "SignalR
 * connection is already established" forever, which made the UI look
 * healthy while no hub was actually reachable.
 *
 * The fix:
 *  - Pull the access token from the Keycloak singleton via
 *    `getValidToken(30)`, refreshing if it expires within 30s. This is
 *    the same path axios uses (see api/index.ts), so SignalR and HTTP
 *    requests share one source of truth for auth.
 *  - Expose a `disconnect()` that drops the singleton and unsubscribes
 *    handlers, called by the logout flow so a subsequent login doesn't
 *    inherit the previous user's stale connection.
 *  - Use `onclose`/`onreconnecting`/`onreconnected` for observability
 *    (we keep the console.warn to match the existing log style).
 *  - Do NOT subscribe a handler here — `general.tsx` owns the
 *    `NotificationHub` handler and re-registers it on mount. Keeping
 *    subscription in one place avoids the duplicate-handler problem
 *    that was firing `setDataNotification` once per page navigation.
 */

// Module-level singleton. Survives SPA route changes (which don't reload
// the module) but gets dropped on full reload / logout via disconnect().
let signalRConnection: signalR.HubConnection | null = null;

const createSignalRConnection =
    async (): Promise<signalR.HubConnection | null> => {
        // Guard: if SIGNALR_URL is empty (REACT_APP_API_BASE_URL not set at
        // build time), there is no hub to connect to. Bail early instead of
        // passing an empty URL to HubConnectionBuilder and hitting a
        // malformed-URL error or, worse, falling through to a wrong host.
        if (!SIGNALR_URL) {
            // eslint-disable-next-line no-console
            console.warn(
                '[signalr] SIGNALR_URL is empty, skipping hub connect'
            );
            return null;
        }

        // Use the Keycloak token, refreshed if it's about to expire. If
        // there is no token (e.g. user landed on a private page without
        // going through Keycloak) we return null and the caller treats it
        // as "no hub" — the user will retry on the next page mount once
        // they're authenticated.
        const token = await getValidToken(30);
        if (!token) {
            // eslint-disable-next-line no-console
            console.warn(
                '[signalr] no valid Keycloak token, skipping hub connect'
            );
            return null;
        }

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(SIGNALR_URL, {
                // Re-resolve the token on every handshake/reconnect so we
                // never send an expired access token to the hub.
                accessTokenFactory: () => keycloak.token ?? '',
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

        connection.onclose(err => {
            // eslint-disable-next-line no-console
            console.warn('[signalr] connection closed', err);
        });
        connection.onreconnecting(err => {
            // eslint-disable-next-line no-console
            console.warn('[signalr] reconnecting', err);
        });
        connection.onreconnected(() => {
            // eslint-disable-next-line no-console
            console.warn('[signalr] reconnected');
        });

        return connection;
    };

/**
 * Idempotent connect. Safe to call from any effect — if a connection
 * already exists in a non-Disconnected state we return it untouched.
 * Returns the connection (or null if there's no token) so callers can
 * register handlers on it.
 */
export const startSignalRConnection =
    async (): Promise<signalR.HubConnection | null> => {
        if (!signalRConnection) {
            signalRConnection = await createSignalRConnection();
        }
        if (!signalRConnection) {
            return null;
        }
        if (
            signalRConnection.state === signalR.HubConnectionState.Disconnected
        ) {
            try {
                await signalRConnection.start();
                // eslint-disable-next-line no-console
                console.warn('[signalr] connected');
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[signalr] failed to start', err);
                // Drop the broken connection so the next call retries from
                // a clean state instead of looping on a half-built object.
                signalRConnection = null;
            }
        } else {
            // eslint-disable-next-line no-console
            console.warn(
                '[signalr] connection already in state',
                signalRConnection.state
            );
        }
        return signalRConnection;
    };

export const getSignalRConnection = (): signalR.HubConnection | null =>
    signalRConnection;

/**
 * Tear down the singleton. Called on logout so a future login (or
 * a different user on the same browser) starts from a clean state.
 * Safe to call when there's no connection.
 */
export const disconnectSignalR = async (): Promise<void> => {
    if (!signalRConnection) return;
    try {
        if (
            signalRConnection.state !== signalR.HubConnectionState.Disconnected
        ) {
            await signalRConnection.stop();
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[signalr] error during disconnect', err);
    } finally {
        signalRConnection = null;
    }
};
